import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  createMuiTheme,
  MuiThemeProvider,
} from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Alert from '../Alert';
import { resetPasswordRequest, getPasswordPolicies } from '../../utils/portalPasswordsClient';
import DocumentTitle from 'react-document-title';
import LoginForm from './LoginForm';

//This theme is used to wrap the outlined textfields, as the ecrion components' theme
//breaks some of their properties and needs to be revised
const LoginFormTheme = (outerTheme) =>
  createMuiTheme({
    ...outerTheme,
    overrides: {
      ...outerTheme.overrides,
      MuiOutlinedInput: {
        ...outerTheme.overrides.MuiOutlinedInput,
        input: null,
      },
    },
    props: {
      ...outerTheme.props,
      MuiInputLabel: null,
    },
  });

const useStyles = makeStyles({
  container: {
    width: 300,
    margin: 'auto',
  },
  alertInfo: {
    textAlign: 'center',
    width: '100%',
  },
  button: {
    textTransform: 'none',
  },
});

function validatePassword(password, passwordConfirmation, policies) {
  if (policies['PasswordLength'] > password.length) {
    return `Password must have at least ${policies['PasswordLength']} characters`;
  }

  if (policies['UpperCase'] > 0 && !/[A-Z]/.test(password)) {
    return 'Password must have at least one uppercase (A-Z)'
  }

  if (policies['LowerCase'] > 0 && !/[a-z]/.test(password)) {
    return 'Password must have at least one lowercase (a-z)'
  }

  if (policies['Digit'] > 0 && !/[0-9]/.test(password)) {
    return 'Password must have at least one digit (0-9)'
  }

  if (policies['Symbol'] > 0 && !/[` !@$%^&*()_+\-=[\]{};'"\\|,.<>/?~]/.test(password)) {
    return 'Password must have at least one symbol (`!@$%^&*()_+-=[]{};\'"\\|,.<>/?~)'
  }

  if (policies['BannedCharacters'] > 0 && /[:#]/.test(password)) {
    return 'Password cannot contain ":" or "#" characters'
  }

  if (password !== passwordConfirmation) {
    return 'Passwords do not match.';
  }

  return '';
};

const ResetPasswordForm = ({ match, location, history, resetToken, context }) => {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [requestFailed, setRequestFailed] = useState('');
  const [requestSucceeded, setRequestSucceeded] = useState(false);
  const [passwordPolicies, setPasswordPolicies] = useState(null);

  useEffect(() => {
    getPasswordPolicies()
      .then((policies) => {
        setPasswordPolicies(policies);
      })
  }, []);

  useEffect(() => {
    if (passwordPolicies && password) {
      const validationMessage = validatePassword(password, passwordConfirmation, passwordPolicies);

      setRequestFailed(validationMessage);
    }
  }, [password, passwordConfirmation, passwordPolicies]);

  const classes = useStyles();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (requestFailed) return;

    resetPasswordRequest(password, resetToken)
      .then(() => {
        setRequestSucceeded(true);
        history.push(location.pathname);
      })
      .catch((error) => setRequestFailed(error.message));
  };

  if (requestSucceeded) {
    return <LoginForm location={location} history={history} match={match} />;
  }

  return (
    <DocumentTitle title="Change your password - Customer Portal">
      <Box mt={6}>
        <Grid container spacing={6} direction="column">
          <Grid item>
            <Typography variant="h6" align="center">
              {context === 'passwordExpired' ?
                'Your password has expired and must be changed' :
                'Please change your password'
              }
            </Typography>
          </Grid>
          <form onSubmit={(e) => handleSubmit(e)}>
            <MuiThemeProvider theme={LoginFormTheme}>
              <Grid
                container
                item
                spacing={1}
                direction="column"
                className={classes.container}
              >
                <Grid item>
                  <TextField
                    required
                    id="newPassword"
                    label="New Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    required
                    id="passwordConfirmation"
                    label="Confirm Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                  />
                </Grid>
                <Grid item align="center">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    Reset password
                  </Button>
                </Grid>

                {requestFailed && (
                  <Grid item>
                    <Alert align="center">{requestFailed}</Alert>
                  </Grid>
                )}
              </Grid>
            </MuiThemeProvider>
          </form>
        </Grid>
      </Box>
    </DocumentTitle>
  );
};

export default ResetPasswordForm;
